import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import ListItemSkeleton from '@app/src/components/Skeleton/ListItem'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import { useStringifyQueryFilters } from '@app/src/hooks/queryState'
import EnvironmentIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/EnvironmentIcon'
import GovernanceIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/GovernanceIcon'
import SocialIcon from '@app/src/pages/Dashboards/TransparencyDashboard/icons/SocialIcon'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { AllTabs } from '@app/src/pages/ResourceExplorer/Content'
import type { Provider } from '@app/src/types/organizations'
import type { Risk } from '@app/src/types/resourceExplorer'
import { RiskStatus, SustainabilityArea } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import { ChevronRightOutlined } from '@mui/icons-material'
import { alpha, Avatar, Box, ListItem, ListItemButton, ListItemText, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router'
import { useDrawer } from '../../Drawer/DrawerContext'

type RiskStatusOverviewProps = {
  provider: Provider
}

const RiskStatusOverview: React.FC<RiskStatusOverviewProps> = ({ provider }) => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { stringifyQueryFilters } = useStringifyQueryFilters()
  const { closeDrawer } = useDrawer()

  const { items: providerRisks, isLoading } = useFetchCollectionWithPost<Risk>({
    key: [FetchKey.RiskScreening, provider.id],
    endpoint: endpoints.riskScreeningProviderRisks,
    payload: {
      filter: [
        {
          name: 'id',
          filters: [
            {
              operator: Operators.EqualTo,
              value: provider.id,
            },
          ],
        },
      ],
      include: [],
      pagination: {
        pageNumber: 1,
        itemsPerPage: 99999,
      },
    },
    options: {
      enabled: Boolean(provider.id),
    },
  })

  const riskCounts = useMemo(() => {
    if (!providerRisks || providerRisks.length === 0) {
      return {
        environmentCount: 0,
        socialCount: 0,
        governanceCount: 0,
      }
    }

    const highExtremeRisks = providerRisks.filter(
      risk => risk.status === RiskStatus.High || risk.status === RiskStatus.Extreme,
    )

    const environmentCount = highExtremeRisks.filter(
      risk => risk.riskType.sustainabilityArea === SustainabilityArea.Environment,
    ).length

    const socialCount = highExtremeRisks.filter(
      risk => risk.riskType.sustainabilityArea === SustainabilityArea.Social,
    ).length

    const governanceCount = highExtremeRisks.filter(
      risk => risk.riskType.sustainabilityArea === SustainabilityArea.Governance,
    ).length

    return {
      environmentCount,
      socialCount,
      governanceCount,
    }
  }, [providerRisks])

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <ListItemSkeleton
          avatar
          listItemAvatarSx={{ minWidth: 64 }}
          avatarProps={{ size: AvatarSize.XL }}
          primary
          secondary
        />
        <ListItemSkeleton
          avatar
          listItemAvatarSx={{ minWidth: 64 }}
          avatarProps={{ size: AvatarSize.XL }}
          primary
          secondary
        />
        <ListItemSkeleton
          avatar
          listItemAvatarSx={{ minWidth: 64 }}
          avatarProps={{ size: AvatarSize.XL }}
          primary
          secondary
        />
      </Stack>
    )
  }

  const sustainabilityAreas = [
    {
      key: SustainabilityArea.Environment,
      icon: <EnvironmentIcon />,
      bgColor: 'success.light',
      label: formatMessage({ id: 'schemas.providerRisk.environmental' }),
      count: riskCounts.environmentCount,
    },
    {
      key: SustainabilityArea.Social,
      icon: <SocialIcon />,
      bgColor: 'warning.light',
      label: formatMessage({ id: 'schemas.providerRisk.social' }),
      count: riskCounts.socialCount,
    },
    {
      key: SustainabilityArea.Governance,
      icon: <GovernanceIcon />,
      bgColor: 'info.light',
      label: formatMessage({ id: 'schemas.providerRisk.governance' }),
      count: riskCounts.governanceCount,
    },
  ]

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} pb={1}>
        <Typography variant="subtitle1" color="text.primary">
          {formatMessage({ id: 'schemas.filter.riskScreening' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'schemas.riskSetting.riskHighAndExtreme' })}
        </Typography>
      </Stack>

      {sustainabilityAreas.map(area => (
        <ListItem key={area.key} disablePadding sx={{ color: 'text.primary' }}>
          <ListItemButton
            sx={{ py: 0.5 }}
            onClick={() => {
              const filters = [
                {
                  name: 'riskStatus',
                  value: [RiskStatus.High, RiskStatus.Extreme],
                  operator: Operators.In,
                },
                {
                  name: 'riskSustainabilityArea',
                  value: [area.key],
                  operator: Operators.In,
                },
              ]

              history.push(
                stringifyQueryFilters({
                  url: generatePath(paths.resourceExplorer, {
                    resourceType: provider.type.toLowerCase(),
                    resourceId: provider.id,
                    activeTabParam: AllTabs.RiskScreening,
                  }),
                  queryParams: {
                    filters,
                  },
                }),
              )
              closeDrawer()
            }}
            disabled={area.count === 0}
          >
            <Avatar
              sx={({ palette }) => ({
                bgcolor: area.count === 0 ? palette.grey[300] : palette.common.white,
                width: 48,
                height: 48,
                mr: 2,
                border: area.count === 0 ? 'none' : `1px solid ${alpha(palette.common.black, 0.12)}`,
                color: palette.common.black,
              })}
              variant="square"
            >
              {area.icon}
            </Avatar>
            <Box width="100%">
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {area.count}{' '}
                      {area.count === 1
                        ? formatMessage({ id: 'schemas.providerRisk.risk' })
                        : formatMessage({ id: 'schemas.providerRisk.risks' })}
                    </Typography>
                  </Stack>
                }
                secondary={area.label}
              />
            </Box>
            <ChevronRightOutlined color="action" />
          </ListItemButton>
        </ListItem>
      ))}
    </Stack>
  )
}

export default RiskStatusOverview
